/**
 * Player-prop rows — the ONE description of how a prop rung becomes a
 * tradable synthetic GameRow, shared by the table (GameLinesSection) and the
 * parent's live-ticket resolver (GameDetail/index.tsx `livePanelGame`).
 *
 * Danielle nfl-player-props R1 DNL-002: a single-market prop (the NFL
 * touchdown binaries are singleton ladders by design; an MLB player listed at
 * one rung is the same shape) has no threshold strip, so
 * `buildLadderBetLineContext` hands the panel no live context and the panel
 * kept the click-time snapshot — a ticket that stopped following the book and
 * stayed actionable after the ticker left the feed. `findLivePlayerPropSynth`
 * is the resolver the parent runs on every store version instead: it walks
 * the CURRENT selector result by the rung's own synth key and rebuilds the
 * synth with the same wording the table used. A missing group, ladder or rung
 * returns null so the parent fails closed (the cfb-game-props DNL-001 rule).
 */
import { hasPropSynthKey, parsePropSynthKey } from '@/lib/sport/propSynthKey';
import { propStatSport, type PropSport } from '@/lib/sport/propStat';
import type { GameRow, PlayerPropStatGroup } from '@/views/ValueDashboard/types';
import { buildPlayerPropSummaries, type PlayerPropSummary } from './playerPropSummary';

/** One prop rung as the table's synthetic GameRow: the outcome and its
 *  complement in the two name slots (the order ticket reads them as YES/NO,
 *  #251), the one book in both team slots of a singleSide row. */
export function buildPlayerPropSynth(game: GameRow, s: PlayerPropSummary): GameRow {
  return {
    ...game,
    key: s.key,
    gameState: 'pre',
    finalWinnerSide: null,
    awayName: s.outcomeLabel,
    homeName: s.notOutcomeLabel,
    kalshi: s.kalshi ? { away: s.kalshi, home: s.kalshi } : null,
    polymarket: s.polymarket ? { away: s.polymarket, home: s.polymarket } : null,
  };
}

/** Re-resolve a prop rung synth from the live selector result. Null when the
 *  key is not a prop synth of this game, or its stat group / ladder / rung
 *  is no longer in the result. */
export function findLivePlayerPropSynth(
  game: GameRow,
  synthKey: string,
  groups: readonly PlayerPropStatGroup[] | null,
): GameRow | null {
  if (!groups) return null;
  const parsed = parsePropSynthKey(synthKey);
  if (!parsed || parsed.gameKey !== game.key) return null;
  const group = groups.find((g) => g.stat === parsed.stat);
  if (!group) return null;
  if (!group.players.some((p) => p.playerKey === parsed.playerKey)) return null;
  const summary = buildPlayerPropSummaries(game, group).find((s) => s.key === synthKey);
  return summary ? buildPlayerPropSynth(game, summary) : null;
}

/** Parent-panel boundary for prop rows. `undefined` = not a prop synth
 *  (continue through the other resolvers); `null` = a prop synth whose base
 *  game, group, ladder or rung is no longer current — the ticket must fail
 *  closed rather than revive the click-time snapshot. The selector is called
 *  once, for the key's own sport and game only. */
export function resolveLivePropPanelGame(
  game: GameRow | null,
  synthKey: string,
  getGroups: (sport: PropSport, gameKey: string) => readonly PlayerPropStatGroup[] | null,
): GameRow | null | undefined {
  if (!hasPropSynthKey(synthKey)) return undefined;
  const parsed = parsePropSynthKey(synthKey);
  if (!parsed) return null;
  if (!game || game.key !== parsed.gameKey) return null;
  return findLivePlayerPropSynth(game, synthKey, getGroups(propStatSport(parsed.stat), parsed.gameKey));
}
